"use client";

import React from 'react';
import { useRouter } from 'next/navigation';
import { useLocale } from '@/contexts/LocaleContext';
import { getPathForLocale } from '@/lib/i18n';
import { getApiBaseUrl } from '@/lib/api';
import { clearToken, getToken } from '@/lib/auth';

type FavoriteState = {
  loading: boolean;
  favorite: boolean;
};

export function FavoriteButton({
  casinoSlug,
  className,
}: {
  casinoSlug: string;
  className?: string;
}) {
  const router = useRouter();
  const locale = useLocale();
  const slug = String(casinoSlug ?? '').trim();
  const loginUrl = `${getPathForLocale(locale, '/login')}?next=${encodeURIComponent(`/casinos/${slug}`)}`;

  const [state, setState] = React.useState<FavoriteState>({
    loading: false,
    favorite: false,
  });
  const [hasToken, setHasToken] = React.useState<boolean>(false);

  React.useEffect(() => {
    const sync = () => {
      const token = getToken();
      setHasToken(Boolean(token));
    };
    sync();
    window.addEventListener('storage', sync);
    window.addEventListener('auth:token', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('auth:token', sync);
    };
  }, []);

  React.useEffect(() => {
    if (!slug) return;
    const token = getToken();
    if (!token) return;

    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetch(
          `${getApiBaseUrl()}/favorites/me/has?casinoSlug=${encodeURIComponent(slug)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (res.status === 401 || res.status === 403) {
          clearToken();
          return;
        }
        if (!res.ok) return;
        const data = (await res.json()) as { favorite?: boolean };
        if (!cancelled) {
          setState((s) => ({ ...s, favorite: Boolean(data.favorite) }));
        }
      } catch {
        // ignore
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [slug, hasToken]);

  const toggle = async () => {
    const token = getToken();
    if (!token) {
      router.push(loginUrl);
      return;
    }

    setState((s) => ({ ...s, loading: true }));
    try {
      const nextFavorite = !state.favorite;
      // optimistic
      setState((s) => ({ ...s, favorite: nextFavorite }));
      const res = await fetch(`${getApiBaseUrl()}/favorites/me/${encodeURIComponent(slug)}`, {
        method: nextFavorite ? 'POST' : 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) {
        clearToken();
        router.push(loginUrl);
        setState((s) => ({ ...s, favorite: !nextFavorite }));
        return;
      }
      if (!res.ok) {
        // rollback
        setState((s) => ({ ...s, favorite: !nextFavorite }));
      }
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const label = state.favorite ? 'Remove from favorites' : 'Add to favorites';
  const base =
    'inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-200 hover:border-white/30 hover:bg-white/10 disabled:opacity-60';
  const active = state.favorite ? 'border-[#ffb74d]/60 bg-[#ffb74d]/10 text-[#ffb74d]' : '';
  const combined = `${base} ${active} ${className ?? ''}`;

  return (
    <button
      type="button"
      onClick={toggle}
      className={combined}
      disabled={state.loading || !slug}
      aria-label={label}
      title={!hasToken ? 'Sign in to save favorites' : label}
    >
      <span aria-hidden className="leading-none">
        {state.favorite ? (
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        ) : (
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        )}
      </span>
    </button>
  );
}

